import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Reply, CheckCircle2, Archive, MailOpen, Mail, BellRing } from "lucide-react";
import { mailApi, fmtDate, gmailLink } from "./mailApi";
import { ReminderDialog } from "../reminders/ReminderDialog";

// Guest / supplier messages (non-newsletter) with no reply from us yet.
export function MailNeedsReply({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [remind, setRemind] = useState(null);

  const load = () => mailApi.needsReply().then(setRows).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);

  const act = async (m, action) => {
    setBusy(m.id);
    try { await mailApi.messagesAction(m.ids || [m.id], action); setRows((r) => r.filter((x) => x.id !== m.id)); onChanged?.(); }
    catch { toast.error("Errore"); } finally { setBusy(null); }
  };

  if (!rows) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#4a1c17]" /></div>;
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-emerald-700 font-medium" data-testid="mail-needs-reply-empty">Tutto risposto: nessuna richiesta in sospeso negli ultimi 90 giorni.</p>;
  return (
    <div className="space-y-2" data-testid="mail-needs-reply-list">
      <p className="text-xs text-[#7c5a4e]">Messaggi di persone (non newsletter) degli ultimi 90 giorni a cui non risulta una tua risposta. Rispondi da Gmail, poi spariranno da soli al prossimo aggiornamento; oppure segnali "gestita".</p>
      {rows.map((m) => (
        <div key={m.id} className={`rounded-xl border px-3 py-2.5 flex flex-wrap items-start gap-x-3 gap-y-2 ${m.seen ? "bg-white border-[#e8dcc8]" : "bg-[#fff6e8] border-[#e2c48f]"}`} data-testid={`mail-reply-${m.uid}`}>
          <div className="mt-0.5 text-[#4a1c17]">{m.seen ? <MailOpen className="h-4 w-4 text-[#9a7f72]" /> : <Mail className="h-4 w-4" />}</div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className="font-semibold text-sm text-[#2b1410]">{m.name || m.email}</span>
              <span className="text-xs text-[#7c5a4e] truncate">{m.email}</span>
              <span className="text-[11px] text-[#9a7f72] ml-auto">{fmtDate(m.ts)}</span>
            </div>
            <div className="text-sm text-[#2b1410] truncate mt-0.5">{m.subject}{m.count > 1 && <span className="ml-2 text-[11px] text-[#9a7f72]">+{m.count - 1} altre</span>}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0 w-full justify-end sm:w-auto">
            <a href={gmailLink(m.message_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-3 py-1 text-xs font-semibold hover:bg-[#2b1410]" data-testid={`mail-reply-open-${m.uid}`}><Reply className="h-3.5 w-3.5" /> Rispondi</a>
            <button onClick={() => setRemind({ text: `${m.name || m.email} – ${m.subject || "ricontattare"}: `, ref_name: m.name || m.email, ref_email: m.email })} title="Ricordamelo" className="p-1.5 rounded-md text-amber-700 hover:bg-amber-50" data-testid={`mail-reply-remind-${m.uid}`}><BellRing className="h-4 w-4" /></button>
            <button disabled={busy === m.id} onClick={() => act(m, "handled")} title="Segna come gestita" className="p-1.5 rounded-md text-emerald-700 hover:bg-emerald-50" data-testid={`mail-reply-handled-${m.uid}`}><CheckCircle2 className="h-4 w-4" /></button>
            <button disabled={busy === m.id} onClick={() => act(m, "archive")} title="Archivia" className="p-1.5 rounded-md text-[#7c5a4e] hover:bg-[#f7ebdf]" data-testid={`mail-reply-archive-${m.uid}`}><Archive className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      {remind && <ReminderDialog open onClose={() => setRemind(null)} preset={remind} />}
    </div>
  );
}
